import React from 'react';
import { Users, Home, BedDouble, Building2, UserPlus, Phone, Mail, Edit, LogIn } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';

const fmtDate = (dateStr) => {
  if (!dateStr) return '';
  const parts = dateStr.split('-');
  if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
  return dateStr;
};

const getRoomIcon = (roomType) => {
  switch(roomType) {
    case 'couple': return BedDouble;
    case 'deluxe': return Building2;
    case 'standard': return Users;
    default: return Home;
  }
};

const getRoomStatusColor = (status) => {
  switch(status) {
    case 'reserved': return 'room-glow-reserved';
    case 'on_hold': return 'room-glow-on_hold';
    default: return 'room-available';
  }
};

export const RoomStatusTab = ({
  rooms, roomsStatus, selectedRoomDate, setSelectedRoomDate,
  handleRoomClick, setShowAddGuestDialog,
  showRoomDetailDialog, setShowRoomDetailDialog, selectedRoom,
  bookings, openEditDialog, openCheckoutDialog, totalGuestsOnDate
}) => {
  return (
    <div data-testid="rooms-content">
      <div className="flex flex-wrap gap-4 mb-6 items-end">
        <div>
          <Label className="text-sm text-[#8E9A95]">Date</Label>
          <Input type="date" value={selectedRoomDate} onChange={(e) => setSelectedRoomDate(e.target.value)} className="mt-1 w-44" />
        </div>
        <Button onClick={() => setShowAddGuestDialog(true)} className="bg-[#D4AF37] hover:bg-[#E5C766]" data-testid="add-guest-btn"><UserPlus size={16} className="mr-2" /> Add Guest</Button>
        <div className="ml-auto bg-[#D4AF37]/10 px-5 py-3 rounded-lg border border-[#D4AF37]/30 flex items-center gap-3">
          <Users size={22} className="text-[#D4AF37]" />
          <div>
            <p className="text-xs text-[#8E9A95]">Total Guests on Property</p>
            <p className="text-2xl font-bold text-[#D4AF37]">{totalGuestsOnDate || 0}</p>
          </div>
        </div>
      </div>
      <div className="flex flex-wrap gap-4 mb-6">
        <div className="flex items-center gap-2"><div className="w-4 h-4 rounded border-2 room-glow-reserved"></div><span className="text-sm text-[#8E9A95]">Reserved</span></div>
        <div className="flex items-center gap-2"><div className="w-4 h-4 rounded border-2 room-glow-on_hold"></div><span className="text-sm text-[#8E9A95]">On Hold</span></div>
        <div className="flex items-center gap-2"><div className="w-4 h-4 rounded border-2 room-available"></div><span className="text-sm text-[#8E9A95]">Available</span></div>
      </div>
      <div className="mb-8">
        <h3 className="font-serif text-lg font-semibold text-[#E8E3D9] mb-4">Rooms</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7 gap-4">
          {rooms.map(room => {
            const status = roomsStatus.find(rs => rs.name === room.name);
            const RoomIcon = getRoomIcon(room.room_type);
            const statusClass = status ? getRoomStatusColor(status.status) : 'bg-[#121816] text-[#8E9A95] border-[#D4AF37]/20';
            const hasBooking = status?.booking;
            return (
              <div
                key={room.id}
                onClick={() => hasBooking && handleRoomClick(status)}
                className={`p-4 rounded-lg border-2 text-center transition-all hover:shadow-lg ${statusClass} ${hasBooking ? 'cursor-pointer' : 'cursor-default'}`}
                title={hasBooking ? `Click to view: ${status.booking.guest_name}` : 'Available'}
                data-testid={`room-icon-${room.name.replace(/\s/g, '-')}`}
              >
                <RoomIcon size={28} className="mx-auto mb-2" />
                <p className="text-sm font-medium">{room.name}</p>
                <p className="text-xs opacity-75 capitalize">{room.room_type}</p>
                {hasBooking && <p className="text-xs mt-1 truncate font-medium">{status.booking.guest_name}</p>}
              </div>
            );
          })}
        </div>
      </div>

      <Dialog open={showRoomDetailDialog} onOpenChange={setShowRoomDetailDialog}>
        <DialogContent data-admin-dialog="true" className="max-w-md bg-[#121816] border-[#D4AF37]/30 text-[#E8E3D9]">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Home size={20} /> {selectedRoom?.name} - Guest Details</DialogTitle></DialogHeader>
          {selectedRoom?.booking && (
            <div className="space-y-4">
              <div className="bg-[#0B0F0E] p-4 rounded-lg">
                <h4 className="font-semibold text-lg text-[#E8E3D9] mb-2">{selectedRoom.booking.guest_name}</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-[#8E9A95]"><Phone size={14} />{selectedRoom.booking.phone}</div>
                  {selectedRoom.booking.email && <div className="flex items-center gap-2 text-[#8E9A95] text-xs truncate"><Mail size={14} /><span className="truncate">{selectedRoom.booking.email}</span></div>}
                </div>
              </div>
              {selectedRoom.booking.rooms_tonight && selectedRoom.booking.rooms_tonight.length > 0 && (
                <div className="text-sm"><p className="text-[#8E9A95] mb-1">Rooms on this date:</p><p className="font-semibold text-[#D4AF37]">{selectedRoom.booking.rooms_tonight.join(', ')}</p></div>
              )}
              {selectedRoom.booking.room_names && selectedRoom.booking.room_names.length > 0 && 
               JSON.stringify(selectedRoom.booking.room_names) !== JSON.stringify(selectedRoom.booking.rooms_tonight) && (
                <div className="text-sm"><p className="text-[#8E9A95] mb-1">All rooms across stay:</p><p className="text-[#E8E3D9]">{selectedRoom.booking.room_names.join(', ')}</p></div>
              )}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><p className="text-[#8E9A95]">Check-in</p><p className="font-medium">{fmtDate(selectedRoom.booking.check_in)}</p></div>
                <div><p className="text-[#8E9A95]">Check-out</p><p className="font-medium">{fmtDate(selectedRoom.booking.check_out)}</p></div>
                <div><p className="text-[#8E9A95]">Guests</p><p className="font-medium">{selectedRoom.booking.num_guests}</p></div>
                <div><p className="text-[#8E9A95]">Status</p><span className={`status-badge status-${selectedRoom.booking.status}`}>{selectedRoom.booking.status}</span></div>
              </div>
              <div className="bg-[#1A2421] p-4 rounded-lg space-y-2 text-sm">
                <div className="flex justify-between"><span>Total Amount:</span><span className="font-medium">₹{(selectedRoom.booking.total_price || 0).toLocaleString()}</span></div>
                <div className="flex justify-between"><span>Restaurant Bill:</span><span className="font-medium text-blue-600">₹{(selectedRoom.booking.restaurant_bill || 0).toLocaleString()}</span></div>
                <div className="flex justify-between"><span>Advance Paid:</span><span className="font-medium text-green-600">₹{(selectedRoom.booking.advance_amount || 0).toLocaleString()}</span></div>
                <div className="flex justify-between"><span>Check-in Collection:</span><span className="font-medium text-green-600">₹{(selectedRoom.booking.checkin_collection || 0).toLocaleString()}</span></div>
                <div className="flex justify-between pt-2 border-t border-[#D4AF37]/30"><span className="font-semibold">Balance Due:</span><span className="font-bold text-[#D4AF37]">₹{(selectedRoom.booking.balance || 0).toLocaleString()}</span></div>
              </div>
              {selectedRoom.booking.special_requests && <p className="text-sm text-[#8E9A95] bg-[#0B0F0E] p-3 rounded"><strong>Notes:</strong> {selectedRoom.booking.special_requests}</p>}
              <div className="flex gap-2 pt-4">
                <Button variant="outline" onClick={() => { setShowRoomDetailDialog(false); const b = bookings.find(bk => bk.id === selectedRoom.booking.id); if(b) openEditDialog(b); }} className="flex-1"><Edit size={16} className="mr-2" /> Edit Booking</Button>
                <Button onClick={() => { setShowRoomDetailDialog(false); const b = bookings.find(bk => bk.id === selectedRoom.booking.id); if(b) openCheckoutDialog(b); }} className="flex-1 bg-[#D4AF37] hover:bg-[#E5C766]"><LogIn size={16} className="mr-2" /> Checkout</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
